import { supabase } from '@/lib/supabase';
import { assertInCoreScope } from '@/lib/coreBoundaries';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 59. MODULARITÉ · 61. MODULE REGISTRY
// Zifek Core DOIT fonctionner même si aucun module avancé n'est activé.
//   Un freelance : Core + Agenda + CRM + Finance
//   Un hôtel    : Core + Hospitality + Booking + Finance
//   Une boutique: Core + Commerce + Inventory + POS + Finance
//
// Le MODULE REGISTRY centralise pour chaque module : status, tenant,
// version, permissions, tools, entities, events. Un module peut être activé
// ou désactivé par entreprise, sans modifier le Core.
// ─────────────────────────────────────────────────────────────

export interface ModuleCatalogEntry {
  key: string;
  name: string;
  category: string;
  icon: string;
  desc: string;
  /** Modules recommandés selon le type d'activité (modularité, spec 59). */
  profiles: string[];
  permissions: string[];
  tools: string[];
  entities: string[];
  events: string[];
  pages: string[];
  isCore?: boolean;
}

export const MODULE_CATEGORIES: { key: string; label: string }[] = [
  { key: 'Socle', label: 'Socle' },
  { key: 'Planification', label: 'Planification' },
  { key: 'Relation client', label: 'Relation client' },
  { key: 'Finance', label: 'Finance' },
  { key: 'Hébergement', label: 'Hébergement' },
  { key: 'Réservation', label: 'Réservation' },
  { key: 'Commerce', label: 'Commerce' },
  { key: 'Stock', label: 'Stock' },
  { key: 'Point de vente', label: 'Point de vente' },
];

/** Catalogue des modules disponibles (métadonnées affichables, source de vérité). */
export const MODULE_CATALOG: ModuleCatalogEntry[] = [
  { key: 'core', name: 'Zifek Core', category: 'Socle', icon: 'ri-stack-line', desc: 'Le socle commun, toujours actif.', profiles: ['freelance', 'hotel', 'shop'], permissions: ['core.read', 'core.write'], tools: ['search_customers'], entities: ['customer', 'contact'], events: ['customer.created'], pages: ['/dashboard/socle'], isCore: true },
  { key: 'agenda', name: 'Zifek Agenda', category: 'Planification', icon: 'ri-calendar-2-line', desc: 'Rendez-vous, disponibilités et planning.', profiles: ['freelance', 'hotel', 'shop'], permissions: ['appointment.read', 'appointment.write'], tools: ['create_appointment'], entities: ['appointment'], events: ['appointment.created'], pages: ['/dashboard/apps/zcalendar'] },
  { key: 'crm', name: 'Zifek CRM', category: 'Relation client', icon: 'ri-contacts-book-2-line', desc: 'Fiches clients et historique.', profiles: ['freelance', 'hotel', 'shop'], permissions: ['customers.read', 'customers.write'], tools: ['search_customers', 'get_customer_orders'], entities: ['customer'], events: ['customer.created'], pages: ['/dashboard/customers'] },
  { key: 'finance', name: 'Zifek Finance', category: 'Finance', icon: 'ri-funds-line', desc: 'Factures, paiements, dépenses.', profiles: ['freelance', 'hotel', 'shop'], permissions: ['finance.read', 'finance.write'], tools: ['get_financial_summary', 'create_invoice'], entities: ['invoice', 'payment'], events: ['invoice.paid'], pages: ['/dashboard/finance'] },
  { key: 'hospitality', name: 'Zifek Hospitality', category: 'Hébergement', icon: 'ri-hotel-line', desc: 'Chambres, appartements, villas.', profiles: ['hotel'], permissions: ['products.read', 'products.write'], tools: ['search_accommodations'], entities: ['property', 'unit'], events: ['booking.created'], pages: ['/dashboard/apps/hebergement'] },
  { key: 'booking', name: 'Zifek Booking', category: 'Réservation', icon: 'ri-calendar-check-line', desc: 'Réservation de ressources et créneaux.', profiles: ['hotel', 'shop'], permissions: ['appointment.read', 'products.write'], tools: ['create_booking', 'reserve_resource'], entities: ['booking'], events: ['booking.confirmed'], pages: ['/booking'] },
  { key: 'commerce', name: 'Zifek Commerce', category: 'Commerce', icon: 'ri-shopping-bag-3-line', desc: 'Produits, panier, commandes.', profiles: ['shop'], permissions: ['orders.read', 'orders.write', 'products.read'], tools: ['search_products', 'create_order'], entities: ['product', 'order'], events: ['order.created'], pages: ['/dashboard/products', '/dashboard/orders'] },
  { key: 'inventory', name: 'Zifek Inventory', category: 'Stock', icon: 'ri-stack-line', desc: 'Mouvements de stock et entrepôts.', profiles: ['shop'], permissions: ['products.read', 'products.write'], tools: ['search_products'], entities: ['stock_movement'], events: ['stock.low'], pages: ['/dashboard/products/stock'] },
  { key: 'pos', name: 'Zifek POS', category: 'Point de vente', icon: 'ri-store-3-line', desc: 'Caisse et reçus.', profiles: ['shop'], permissions: ['orders.write', 'products.read'], tools: ['create_order'], entities: ['sale'], events: ['sale.completed'], pages: ['/dashboard/caisse'] },
];

export function moduleCatalogMeta(key: string | null | undefined): ModuleCatalogEntry {
  return MODULE_CATALOG.find((m) => m.key === key) || {
    key: key || 'unknown', name: key || 'Module', category: 'Autre', icon: 'ri-apps-2-line',
    desc: 'Module personnalisé.', profiles: [], permissions: [], tools: [], entities: [], events: [], pages: [],
  };
}

export interface CoreModule {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  module_key: string;
  name: string | null;
  category: string | null;
  version: string | null;
  status: string | null;
  is_core: boolean | null;
  is_optional: boolean | null;
  permissions: string[] | null;
  tools: string[] | null;
  entities: string[] | null;
  events: string[] | null;
  pages: string[] | null;
  workflows: string[] | null;
  settings: string[] | null;
  config: Record<string, unknown> | null;
  installed_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

function asArray(v: unknown): string[] {
  if (Array.isArray(v)) return v as string[];
  return [];
}

/** Liste les modules (niveau plateforme + ceux de l'entreprise), fusionnés par clé. */
export async function listModules(organizationId: number): Promise<CoreModule[]> {
  const { data, error } = await supabase
    .from('core_modules')
    .select('*')
    .or(`organization_id.is.null,organization_id.eq.${organizationId}`)
    .order('is_core', { ascending: false })
    .order('module_key', { ascending: true });
  if (error) throw error;

  const byKey = new Map<string, CoreModule>();
  (data || []).forEach((row) => {
    const m = row as CoreModule;
    const existing = byKey.get(m.module_key);
    // La ligne de l'entreprise (plus spécifique) écrase la ligne plateforme.
    if (!existing || (m.organization_id != null)) {
      byKey.set(m.module_key, {
        ...m,
        permissions: asArray(m.permissions),
        tools: asArray(m.tools),
        entities: asArray(m.entities),
        events: asArray(m.events),
        pages: asArray(m.pages),
        workflows: asArray(m.workflows),
        settings: asArray(m.settings),
      });
    }
  });
  // Garantit la présence de tout le catalogue (modularité : le Core marche seul).
  MODULE_CATALOG.forEach((cat) => {
    if (!byKey.has(cat.key)) {
      byKey.set(cat.key, {
        id: -1, tenant_id: null, organization_id: null, module_key: cat.key, name: cat.name,
        category: cat.category, version: '1.0', status: cat.isCore ? 'enabled' : 'disabled',
        is_core: !!cat.isCore, is_optional: !cat.isCore, permissions: cat.permissions, tools: cat.tools,
        entities: cat.entities, events: cat.events, pages: cat.pages, workflows: [], settings: [],
        config: null, installed_at: null, created_at: null, updated_at: null,
      });
    }
  });
  return Array.from(byKey.values());
}

/** Modules réellement activés pour l'entreprise (le Core est toujours actif). */
export async function getEnabledModuleKeys(organizationId: number): Promise<string[]> {
  const modules = await listModules(organizationId);
  return modules.filter((m) => m.is_core || m.status === 'enabled').map((m) => m.module_key);
}

export interface EnableModuleInput {
  tenantId?: number | null;
  organizationId: number;
  moduleKey: string;
  version?: string;
  config?: Record<string, unknown> | null;
}

/** Active (ou met à jour) un module pour une entreprise. */
export async function enableModule(input: EnableModuleInput): Promise<void> {
  const cat = moduleCatalogMeta(input.moduleKey);
  const existing = await supabase
    .from('core_modules')
    .select('id')
    .eq('organization_id', input.organizationId)
    .eq('module_key', input.moduleKey)
    .maybeSingle();

  const payload = {
    tenant_id: input.tenantId ?? null,
    organization_id: input.organizationId,
    module_key: input.moduleKey,
    name: cat.name,
    category: cat.category,
    version: input.version || '1.0',
    status: 'enabled',
    is_core: !!cat.isCore,
    is_optional: !cat.isCore,
    permissions: cat.permissions,
    tools: cat.tools,
    entities: cat.entities,
    events: cat.events,
    pages: cat.pages,
    workflows: [],
    settings: [],
    config: input.config ?? null,
    installed_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };
  if (existing.data?.id) {
    const { error } = await supabase.from('core_modules').update(payload).eq('id', existing.data.id);
    if (error) throw error;
  } else {
    const { error } = await supabase.from('core_modules').insert(payload);
    if (error) throw error;
  }
}

export async function disableModule(organizationId: number, moduleKey: string): Promise<void> {
  const { error } = await supabase
    .from('core_modules')
    .update({ status: 'disabled', updated_at: new Date().toISOString() })
    .eq('organization_id', organizationId)
    .eq('module_key', moduleKey);
  if (error) throw error;
}

export async function setModuleVersion(organizationId: number, moduleKey: string, version: string): Promise<void> {
  const { error } = await supabase
    .from('core_modules')
    .update({ version, updated_at: new Date().toISOString() })
    .eq('organization_id', organizationId)
    .eq('module_key', moduleKey);
  if (error) throw error;
}

/** Enregistre un NOUVEAU module (extension) sans toucher au Core (spec 60). */
export async function registerModule(input: {
  tenantId?: number | null;
  organizationId: number;
  moduleKey: string;
  name: string;
  category?: string;
  version?: string;
  permissions?: string[];
  tools?: string[];
  entities?: string[];
  events?: string[];
  pages?: string[];
}): Promise<void> {
  // Garde-fou (spec 74) : on n'enregistre PAS comme module une capacité qui est
  // déjà une fondation du Core — le Core reste stable et non dupliqué.
  const scope = await assertInCoreScope(input.moduleKey);
  if (scope.ownership === 'core') {
    throw new Error(
      `« ${input.moduleKey} » est une fondation du Core, pas un module. Le Core fournit déjà cette brique : ne la recréez pas.`,
    );
  }
  const { error } = await supabase.from('core_modules').insert({
    tenant_id: input.tenantId ?? null,
    organization_id: input.organizationId,
    module_key: input.moduleKey,
    name: input.name,
    category: input.category || 'Autre',
    version: input.version || '1.0',
    status: 'enabled',
    is_core: false,
    is_optional: true,
    permissions: input.permissions || [],
    tools: input.tools || [],
    entities: input.entities || [],
    events: input.events || [],
    pages: input.pages || [],
    installed_at: new Date().toISOString(),
  });
  if (error) throw error;
}

export interface ModuleStats {
  total: number;
  enabled: number;
  disabled: number;
  byCategory: Record<string, number>;
}

export async function fetchModuleStats(organizationId: number): Promise<ModuleStats> {
  const modules = await listModules(organizationId);
  const byCategory: Record<string, number> = {};
  let enabled = 0;
  modules.forEach((m) => {
    byCategory[m.category || 'Autre'] = (byCategory[m.category || 'Autre'] || 0) + 1;
    if (m.is_core || m.status === 'enabled') enabled += 1;
  });
  return { total: modules.length, enabled, disabled: modules.length - enabled, byCategory };
}

/** Modules recommandés pour un profil d'activité (modularité, spec 59). */
export function recommendedModulesFor(profile: string): ModuleCatalogEntry[] {
  return MODULE_CATALOG.filter((m) => m.profiles.includes(profile));
}

export const BUSINESS_PROFILES: { key: string; label: string; icon: string; desc: string }[] = [
  { key: 'freelance', label: 'Freelance / Prestataire', icon: 'ri-user-star-line', desc: 'Core + Agenda + CRM + Finance' },
  { key: 'hotel', label: 'Hôtel / Hébergement', icon: 'ri-hotel-line', desc: 'Core + Hospitality + Booking + Finance' },
  { key: 'shop', label: 'Boutique / Commerce', icon: 'ri-store-3-line', desc: 'Core + Commerce + Inventory + POS + Finance' },
];